import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Globe,
  FileText,
  PhoneMissed,
  Headphones,
  Layers,
  CalendarCheck,
  SearchCheck,
  Smartphone,
  ArrowUpRight,
  ArrowRight,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import PageHero from "@/components/site/PageHero";
import ContractorInquiryForm from "@/components/site/ContractorInquiryForm";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/dd575c7557e7d72528f8d94bb53752a0ac5d546bb142e6d9098a6c4d0ab73cf8.png";
const LEAD_CARD_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/d4f1e4eab0ebe102b290e0d034845b0ff38508133a3e424b175f41c5f77fb1cc.png";
const AI_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/bb4a9f0222b9482b3cb9b7e6623ebc30ad65f40b81c8b43f9745f5e0dea26129.png";

const SHOWS = [
  {
    icon: Globe,
    title: "Custom HVAC website",
    desc: "A polished, mobile-first site built to actually convert SWFL homeowners.",
  },
  {
    icon: FileText,
    title: "Smart service-request intake",
    desc: "A guided form that captures the right details so contractors triage with context.",
  },
  {
    icon: PhoneMissed,
    title: "Missed-call follow-up concept",
    desc: "Automatic SMS that keeps the lead warm whenever the office is unavailable.",
  },
  {
    icon: Headphones,
    title: "AI receptionist concept",
    desc: "A backup voice intake that collects name, callback, issue, urgency, and city.",
  },
  {
    icon: Layers,
    title: "CRM-ready lead summary",
    desc: "Every submission shows up as a clean lead card the dispatcher can act on.",
  },
  {
    icon: CalendarCheck,
    title: "Maintenance lead capture",
    desc: "Recurring revenue starts with capturing interest — not chasing it later.",
  },
  {
    icon: SearchCheck,
    title: "Local SEO / AEO structure",
    desc: "Service-area pages, FAQ schema-ready content, and clear topical depth.",
  },
  {
    icon: Smartphone,
    title: "Mobile-first design",
    desc: "Because most homeowners search from their phone in the hottest moment of the day.",
  },
];

const REASONS = [
  "Busy owners miss calls during real service runs.",
  "Leads come in scattered across phone, text, web, and email.",
  "Customers don't always describe the problem clearly upfront.",
  "Maintenance requests slip through the cracks.",
  "Replacement inquiries need better qualification before a sales call.",
];

const BUILD = [
  "Custom contractor website",
  "Service request forms",
  "Call / text follow-up workflows",
  "PrismForge Dispatch CRM setup",
  "Make automations",
  "AI receptionist / intake concepts",
  "Booking and lead routing systems",
];

// Lazy import to keep page tree clean
export default function Demo() {
  useSeo({
    title:
      "Built by PrismForge Digital — see how this HVAC demo works | Sun Chaser Cooling",
    description:
      "A working sample of the HVAC website, smart intake, missed-call follow-up, AI receptionist concept, and CRM-ready lead summary PrismForge Digital builds for contractors.",
  });

  return (
    <div data-testid="demo-page">
      <PageHero
        eyebrow="Built by PrismForge Digital"
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Demo" }]}
        title="See how this"
        highlight="HVAC demo works."
        subtitle="This site is a working sample of the kind of HVAC website, smart intake, and lead-capture workflows PrismForge Digital builds for contractors and home-service businesses."
        primaryCta={{ label: "Get the walkthrough", to: "#contractor-form" }}
        secondaryCta={{
          label: "See live demo leads",
          to: "/demo/leads",
        }}
        image={HERO_IMG}
        imageAlt="Southwest Florida home at golden hour — the kind of audience this HVAC demo is built for."
        variant="navy"
        testId="demo-page-hero"
      />

      {/* What this demo shows */}
      <section
        data-testid="demo-shows-section"
        aria-labelledby="demo-shows-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
              What this demo shows
            </span>
            <h2
              id="demo-shows-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Eight pieces of a modern HVAC presence — already wired up.
            </h2>
            <p className="mt-4 text-base text-navy/70 sm:text-lg">
              Every section of this site maps to a specific revenue or
              retention problem most contractors deal with.
            </p>
          </div>

          <motion.ul
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.05 } },
            }}
            className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            {SHOWS.map((s) => {
              const Icon = s.icon;
              const slug = s.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "");
              return (
                <motion.li
                  key={s.title}
                  data-testid={`demo-shows-card-${slug}`}
                  variants={{
                    hidden: { opacity: 0, y: 14 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
                  }}
                  className="flex flex-col rounded-2xl border border-mist-line bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-aqua">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="mt-4 font-display text-base font-bold leading-tight text-navy">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/65">
                    {s.desc}
                  </p>
                </motion.li>
              );
            })}
          </motion.ul>
        </div>
      </section>

      {/* Why HVAC companies need this */}
      <section
        data-testid="demo-why-section"
        aria-labelledby="demo-why-heading"
        className="relative bg-mist py-20 sm:py-24"
      >
        <div className="container-page grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-6">
            <span className="pill">
              <span className="h-1.5 w-1.5 rounded-full bg-sun" />
              Why HVAC companies need this
            </span>
            <h2
              id="demo-why-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              The five places leads usually leak.
            </h2>
            <p className="mt-4 max-w-lg text-base text-navy/70 sm:text-lg">
              Most contractor websites are pretty &mdash; but they don&rsquo;t fix the
              actual revenue problems. These do.
            </p>
            <ul className="mt-7 space-y-3">
              {REASONS.map((r, i) => (
                <li
                  key={r}
                  className="flex items-start gap-3 rounded-2xl border border-mist-line bg-white p-4 shadow-soft"
                >
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-aqua/15 font-display text-sm font-bold text-aqua-deep">
                    {i + 1}
                  </span>
                  <p className="text-[15px] text-navy/85">{r}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="relative overflow-hidden rounded-3xl border border-mist-line bg-white shadow-lift">
              <img
                src={LEAD_CARD_IMG}
                alt="Illustration of a contractor lead summary card surfacing the clean data captured by the smart intake."
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <div
                className="absolute inset-0 bg-gradient-to-tr from-navy/30 via-transparent to-transparent"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What PrismForge builds */}
      <section
        data-testid="demo-build-section"
        aria-labelledby="demo-build-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-6">
            <div className="relative overflow-hidden rounded-3xl border border-mist-line bg-mist shadow-lift">
              <img
                src={AI_IMG}
                alt="Illustration of an AI voice receptionist concept handling an incoming HVAC call."
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <span className="pill">
              <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
              What PrismForge Digital builds
            </span>
            <h2
              id="demo-build-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              The full system, not just a website.
            </h2>
            <p className="mt-4 max-w-lg text-base text-navy/70 sm:text-lg">
              Custom contractor websites, smart intake forms, follow-up
              workflows, CRM setup, and automations &mdash; all stitched
              together so leads actually become booked jobs.
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {BUILD.map((b) => (
                <li
                  key={b}
                  className="flex items-center gap-2 rounded-xl border border-mist-line bg-white px-3.5 py-2.5 text-sm font-medium text-navy/85 shadow-soft"
                >
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-aqua" />
                  {b}
                </li>
              ))}
            </ul>

            <a
              href="https://prismforgedigital.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="demo-prismforge-external"
              className="group mt-7 inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white px-5 py-3 text-sm font-semibold text-navy shadow-soft transition-colors hover:border-aqua/40"
            >
              prismforgedigital.com
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>
      </section>

      {/* Contractor capture form */}
      <section
        id="contractor-form"
        data-testid="contractor-form-section"
        aria-labelledby="contractor-form-heading"
        className="relative bg-mist-soft py-20 sm:py-24"
      >
        <div className="container-page grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <span className="pill">
              <span className="h-1.5 w-1.5 rounded-full bg-sun" />
              Contractor capture
            </span>
            <h2
              id="contractor-form-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Want this for your business?
            </h2>
            <p className="mt-4 text-base text-navy/70">
              Send a short note and PrismForge Digital will follow up about
              building a website + smart intake system for your contractor
              brand.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-mist-line bg-white px-3 py-1.5 text-xs font-medium text-navy/65 shadow-soft">
              <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
              Demo capture &mdash; goes to PrismForge Digital, not Sun Chaser.
            </p>
          </div>
          <div className="lg:col-span-8">
            <ContractorInquiryForm />
          </div>
        </div>
      </section>

      {/* Final CTA strip — direct to PrismForge */}
      <section
        data-testid="demo-final-cta-section"
        aria-labelledby="demo-final-cta-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="relative overflow-hidden rounded-3xl border border-sun/30 bg-brand-gradient p-8 text-white shadow-lift sm:p-12">
            <div className="absolute -right-12 -top-12 h-56 w-56 rounded-full bg-sun/20 blur-3xl" aria-hidden="true" />
            <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <h2
                  id="demo-final-cta-heading"
                  className="font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl"
                >
                  Ready to build something like this?
                </h2>
                <p className="mt-3 max-w-xl text-base text-white/80">
                  Schedule a free systems call with PrismForge Digital and
                  we&rsquo;ll walk through your current setup and what to
                  upgrade first.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row lg:col-span-5 lg:justify-end">
                <a
                  href="https://prismforgedigital.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="demo-final-cta-systems-call"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-sun px-6 py-3.5 text-sm font-semibold text-navy-deep shadow-sunGlow transition-transform hover:scale-[1.02]"
                >
                  Request a Free Systems Call
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </a>
                <Link
                  to="/about"
                  data-testid="demo-final-cta-about"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white/95 px-6 py-3.5 text-sm font-semibold text-navy shadow-soft transition-colors hover:bg-white"
                >
                  About this demo brand
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
